import React, { useState, useEffect } from 'react';
import { getPlatform } from '@/hooks';
import { useTranslation } from 'react-i18next';

const desktop_base_class = 'm2g-order-confirmation-payment';
const mobile_base_class = 'm2g-order-confirmation-footer';

const {
    _class: {
        Common: { Price },
    },
    Order,
} = Mobi2Go;

const BalanceSpend = ({ balance, currentRebate, formattedBalanceName }) => {
    const { reference, amount, display_amount, translator_base_key, name, is_monetary, is_frozen } = balance;
    const [t] = useTranslation('order.balances');
    const [isUpdating, setIsUpdating] = useState(false);
    const [checkedState, setCheckedState] = useState(false);
    const [rebate, setRebate] = useState(null);

    const pricedAmount = () => {
        let pricedAmount = new Price(0.0);

        // If a rebate has been applied to the order, show the discounted amount.
        if (rebate) {
            pricedAmount.set(-parseFloat(rebate.order_amount_discounted));
        }
        // Otherwise, show the balance.
        else {
            pricedAmount.set(amount / 100);
        }

        return pricedAmount;
    };

    const handleCheckboxClick = async e => {
        e.preventDefault();

        if (isUpdating) {
            // fail safe to prevent multiple calls
            return;
        }

        setIsUpdating(true);

        try {
            if (!checkedState) {
                const claimedRebate = await Order.claimRebate(reference);
                setRebate(claimedRebate);
            } else {
                if (rebate) {
                    await Order.cancelRebate(rebate.id);
                    setRebate(null);
                }
            }

            setCheckedState(!checkedState);

            Order.trigger('updateRebates', 1);
        } catch (e) {
            console.log(e);
        } finally {
            setIsUpdating(false);
        }
    };

     useEffect(() => {
        setCheckedState(!!currentRebate);
        setRebate(currentRebate);
     }, [currentRebate]);

    if (is_frozen) {
        // TODO: indicate the balance is here but frozen
        // Until then we are not going to render it so people can't
        // claim it and hit the backend error.

        return <></>;
    }

    if (getPlatform() === 'web') {
        return (
            <div
                className="m2g-order-confirmation-payment-method m2g-order-confirmation-payment-credit"
                data-customer-balance={formattedBalanceName}
                data-checked={checkedState}
                aria-checked={checkedState}
                aria-disabled={isUpdating}
                onClick={e => handleCheckboxClick(e)}
                id={`customer-balance-${formattedBalanceName}`}
            >
                {isUpdating ? ( // if fetching display loading spinner in place of checkbox
                    <div
                        className={`${desktop_base_class}-credit__loading-spinner m2g-icon m2g-icon--spinner m2g-list-cell__icon`}
                    />
                ) : (
                    <div
                        className={`${desktop_base_class}-credit-checkbox m2g-icon m2g-icon--checkbox m2g-icon--tick`}
                    />
                )}
                <label className={`${desktop_base_class}-credit-label`}>
                    {t(`${translator_base_key}_balance_spend`, {
                        balance_key: reference[1],
                        balance_name: name
                    })}
                </label>

                <div className={`${desktop_base_class}-credit-value`}>
                    <span className="m2g-text--small m2g-text--offset">
                        {is_monetary ? t('price', { price: pricedAmount() }) : t('balance', { balance: display_amount })}
                        {rebate ? '' : ' ' + t('available')}
                    </span>
                </div>
            </div>
        );
    } else {
        return (
            <div
                className={`${mobile_base_class}-credit m2g-list-cell m2g-touchable`}
                data-customer-balance={formattedBalanceName}
                data-checked={checkedState}
                aria-checked={checkedState}
                aria-disabled={isUpdating}
                onClick={e => handleCheckboxClick(e)}
                id={`customer-balance-${formattedBalanceName}`}
            >
                {isUpdating ? (
                    <div
                        className={`${mobile_base_class}__loading-spinner m2g-icon m2g-icon--spinner m2g-list-cell__icon`}
                    />
                ) : (
                    <div className="m2g-list-cell__icon m2g-icon m2g-icon--checkbox m2g-icon--tick" />
                )}
                <div
                    className={`${mobile_base_class}-credit-content m2g-list-cell__content m2g-list-cell__content--small`}
                >
                    <div className={`${mobile_base_class}-credit-label`}>
                        {t(`${translator_base_key}_balance_spend`, {
                            balance_key: reference[1],
                            balance_name: name
                        })}
                    </div>
                    <div className={`${mobile_base_class}-credit-value`}>
                        {is_monetary
                            ? t('price', {
                                price: pricedAmount(),
                            })
                            : t('balance', {
                                balance: display_amount
                            })
                        }
                        {rebate ? '' : ' ' + t('available')}
                    </div>
                </div>
            </div>
        );
    }
};

export default BalanceSpend;
